// vitest からは Nuxt auto-import が効かないため明示 import に。
import { readonly, ref, watchEffect, type Ref } from 'vue'
import {
  bookBookingMenu,
  getBookingMenus,
  getBookingTimeSlotsByMenu,
} from './api/booking-entry'
import type { Store } from './store'
import type { CreateMemberRequest } from './group'
import type { SubProfileTarget } from './sub-profile'
import type { Form as UserAttributesForm } from '~/components/sign-up/user-attribute-form.vue'
import type { RelatedStore } from '~/composables/related-store'
import { storage } from '~/utils/session-storage'
import type { GetBookingMenuList200 } from '~/types/api/BookingMenu'
import type { CamelCaseKeys } from '~/types/util'

export type Menu = CamelCaseKeys<GetBookingMenuList200>[number]

export interface BookingEntry {
  store?: RelatedStore | Store
  menu?: Menu
  note?: string
  fromAt?: Date
  userForm?: UserAttributesForm
  /** 代理予約の対象者(本人 or サブメンバー)。未選択時は本人として扱う。 */
  bookingTarget?: SubProfileTarget
  /**
   * ゲスト(未ログイン)フローで確定前にローカル収集するグループ情報。
   * 確定時にまとめて createGroup / addMember する。
   */
  guestGroupName?: string
  guestMembers?: CreateMemberRequest[]
  /**
   * 予約対象者のローカル参照。member の index は guestMembers の並びに対応する。
   * 確定時に作成済みメンバーの pgm id へ解決する。
   */
  guestTargetRef?: { kind: 'main' } | { kind: 'member'; index: number }
  /**
   * 確定時のグループ/メンバー作成が完了したことを表す冪等化フラグ。
   * partial-failure 後の再 submit で createGroup/addMember を二重実行しないために使う。
   */
  guestGroupConfirmed?: boolean
}

// kubb は OpenAPI patternProperties を `'^\\d{4}-...$'` のような正規表現
// キーで生成するため、実コードで扱うには不便。日付/時刻文字列を直接
// キーとして使う practical alias で扱う。
// 予約枠(日付→時刻→空き)。生成 atom TimeSlots と同データだが、生成型は
// patternProperties 由来で obj[date] の index 参照ができない(Object.keys/entries 利用と
// 不適合)。ergonomic な index-signature でローカル定義する。
export interface TimeSlot {
  [date: string]: {
    [time: string]: boolean
  }
}

const customStorage = storage<BookingEntry>('bookingEntry')
const bookingEntry = ref(customStorage.getItem()) as Ref<BookingEntry>
if (bookingEntry.value.fromAt) bookingEntry.value.fromAt = new Date(bookingEntry.value.fromAt)
watchEffect(() => customStorage.setItem(bookingEntry.value))

const input = ({
  store,
  menu,
  note,
  fromAt,
  userForm,
  bookingTarget,
  guestGroupName,
  guestMembers,
  guestTargetRef,
  guestGroupConfirmed,
}: BookingEntry) => {
  if (store) bookingEntry.value.store = store
  if (menu) bookingEntry.value.menu = menu
  if (note) bookingEntry.value.note = note
  if (fromAt) bookingEntry.value.fromAt = fromAt
  if (userForm) bookingEntry.value.userForm = userForm
  if (bookingTarget) bookingEntry.value.bookingTarget = bookingTarget
  if (guestGroupName) bookingEntry.value.guestGroupName = guestGroupName
  if (guestMembers) bookingEntry.value.guestMembers = guestMembers
  if (guestTargetRef) bookingEntry.value.guestTargetRef = guestTargetRef
  if (guestGroupConfirmed) bookingEntry.value.guestGroupConfirmed = guestGroupConfirmed
  return readonly(bookingEntry)
}

const reset = () => {
  bookingEntry.value = {}
  customStorage.removeItem()
}

const complete = () => {
  reset()
}

const getMenus = async (storeIdAlias: string) => {
  return await getBookingMenus(storeIdAlias)
}

const getTimeSlotsByMenu = async ({
  storeIdAlias,
  menuId,
  fromOn,
  toOn,
  targetGroupMemberId,
}: {
  storeIdAlias: string
  menuId: number
  fromOn: Date
  toOn: Date
  targetGroupMemberId?: number
}) => {
  return await getBookingTimeSlotsByMenu({
    storeIdAlias,
    menuId,
    fromOn,
    toOn,
    targetGroupMemberId,
  })
}

const entry = async () => {
  const storeIdAlias = bookingEntry.value.store!.idAlias
  const menuId = bookingEntry.value.menu!.id
  const fromAt = bookingEntry.value.fromAt!.toISOString()
  const note = bookingEntry.value.note
  // 対象者の pgm id(本人=メイン / メンバー=サブメンバー)。未選択時は undefined=本人。
  const targetGroupMemberId = bookingEntry.value.bookingTarget?.proxyGroupMemberId

  return await bookBookingMenu({
    storeIdAlias,
    menuId,
    fromAt,
    note,
    targetGroupMemberId,
  })
}

// 予約フローの受付停止(PRD F9)。backend は book! / search_time_slots! の入口ガードで
// status 409 + code 'E_STORE_SUSPENDED' を返す(bowmore_public_api handle_errors.rb)。
export const STORE_SUSPENDED_CODE = 'E_STORE_SUSPENDED'

// 予約フロー(枠チェック=time_slots / book)の失敗を可視化する。event handler / watcher 由来の
// throw は onErrorCaptured(Sentry 転送)に吸われ無反応になるため、showError で error.vue を明示描画する。
// - 受付停止(E_STORE_SUSPENDED): 受付停止医院への予約は不可なので 404 画面へ(BUG-1481 の仕様に合わせる)。
// - それ以外: statusCode に応じた汎用エラー(error.vue)へ。
// 予約枠重複(E001)は各確認画面がモーダルで別処理するため、ここには渡さない。
const handleBookingError = (error: unknown) => {
  const e = error as { statusCode?: number; data?: { code?: string } }
  if (e?.data?.code === STORE_SUSPENDED_CODE) {
    showError({ statusCode: 404 })
    return
  }
  showError({ statusCode: e?.statusCode ?? 500 })
}

export const useBookingEntry = () => {
  return {
    bookingEntry: readonly(bookingEntry) as Readonly<Ref<BookingEntry>>,
    getMenus,
    getTimeSlotsByMenu,
    entry,
    input,
    reset,
    complete,
    handleBookingError,
  }
}
